/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { useChartDataCursor } from '../UNSAFE_useChartDataCursor';
import { useChartDatatip } from '../PRIVATE_useChartDatatip';
import { ChartItem } from '../../UNSAFE_Chart/chart.types';

type DataCursorProps = Parameters<typeof useChartDataCursor>[0];
type DatatipProps<K extends string | number, D extends ChartItem<K>> = Parameters<
  typeof useChartDatatip<K, D>
>[0];

export function useChartDatatipAndCursor<K extends string | number, D extends ChartItem<K>>({
  series,
  groups,
  width,
  height,
  findNearest,
  onDataCursorPositionChange,
  isDataCursorEnabled,
  dataCursorPosition,
  dataCursorStyle,
  hoveredItemInfo,
  focusedItemInfo,
  getDataItem,
  getDataItemPos,
  datatip,
  valueFormats,
  isHoriz,
  xScale,
  yScale,
  plotAreaSpace
}: DataCursorProps & DatatipProps<K, D>) {
  const { datatipContent, datatipProps } = useChartDatatip<K, D>({
    series,
    groups,
    width,
    height,
    isDataCursorEnabled,
    dataCursorPosition,
    hoveredItemInfo,
    getDataItem,
    getDataItemPos,
    focusedItemInfo,
    datatip,
    valueFormats,
    isHoriz,
    xScale,
    findNearest
  });

  const { dataCursorContent, dataCursorProps } = useChartDataCursor({
    findNearest,
    dataCursorPosition,
    onDataCursorPositionChange,
    isDataCursorEnabled,
    dataCursorStyle,
    isHoriz,
    focusedItemInfo,
    plotAreaSpace,
    yScale,
    xScale,
    getDataItemPos
  });

  return {
    datatipContent,
    dataCursorContent,
    datatipProps: mergeProps(dataCursorProps, datatipProps)
  };
}
